import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/AuthContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Loader2, Fingerprint, AlertCircle, KeyRound } from "lucide-react";

export default function DiscordCallback() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const { login } = useAuth();

  const [status, setStatus] = useState("processing");
  const [error, setError] = useState("");
  const [debugInfo, setDebugInfo] = useState({});
  const [discordData, setDiscordData] = useState(null);
  const [needsFounder, setNeedsFounder] = useState(false);
  const [inviteCode, setInviteCode] = useState("");
  const [hexId, setHexId] = useState("");
  const [displayName, setDisplayName] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    const code = params.get("code");
    const errParam = params.get("error");
    const errDesc = params.get("error_description");
    const allParams = {};
    for (const [k, v] of params.entries()) allParams[k] = v;
    setDebugInfo({ params: allParams, full_url: window.location.href, pathname: window.location.pathname });

    if (errParam) {
      setStatus("error");
      setError(`Discord hatası: ${errDesc || errParam}`);
      return;
    }
    if (!code) {
      setStatus("error");
      setError(
        "Discord'dan yetkilendirme kodu (code) alınmadı. Olası sebepler:\n" +
        "1) Discord Developer Portal → OAuth2 → Redirects listesinde bu URL tam olarak kayıtlı değil.\n" +
        "2) Client ID yanlış.\n" +
        "3) Discord ekranında 'İptal' veya geri butonuna basıldı."
      );
      return;
    }
    (async () => {
      try {
        const res = await api.post("/auth/discord/callback", { code });
        if (res.data.registered) {
          login(res.data.token, res.data.user);
          toast.success("Discord ile giriş başarılı");
          navigate("/", { replace: true });
          return;
        }
        setDiscordData(res.data);
        setDisplayName(res.data.discord_username);
        // Check if system has a founder; if not, we offer bootstrap-as-founder
        try {
          const st = await api.get("/auth/system-status");
          setNeedsFounder(!st.data.founder_exists);
        } catch (_) { /* ignore */ }
        setStatus("needs_registration");
      } catch (e) {
        setStatus("error");
        setError(e.response?.data?.detail || "Discord doğrulaması başarısız (token exchange)");
      }
    })();
    // eslint-disable-next-line
  }, []);

  const completeRegistration = async () => {
    if (!inviteCode.trim() || !hexId.trim()) return toast.error("Davet kodu ve Hex ID gerekli");
    setSubmitting(true);
    try {
      const res = await api.post("/auth/register", {
        invite_code: inviteCode.trim().toUpperCase(),
        discord_id: discordData.discord_id,
        discord_username: discordData.discord_username,
        discord_avatar: discordData.discord_avatar,
        hex_id: hexId.trim(),
        display_name: displayName.trim() || null,
      });
      login(res.data.token, res.data.user);
      toast.success("Kayıt tamamlandı");
      navigate("/", { replace: true });
    } catch (e) {
      toast.error(e.response?.data?.detail || "Kayıt başarısız");
    } finally { setSubmitting(false); }
  };

  const completeBootstrap = async () => {
    if (!hexId.trim()) return toast.error("Hex ID gerekli");
    setSubmitting(true);
    try {
      const res = await api.post("/auth/bootstrap-founder", {
        invite_code: "BOOTSTRAP",
        discord_id: discordData.discord_id,
        discord_username: discordData.discord_username,
        discord_avatar: discordData.discord_avatar,
        hex_id: hexId.trim(),
        display_name: displayName.trim() || discordData.discord_username,
      });
      login(res.data.token, res.data.user);
      toast.success("Kurucu hesabı oluşturuldu");
      navigate("/", { replace: true });
    } catch (e) {
      toast.error(e.response?.data?.detail || "Kurulum başarısız");
    } finally { setSubmitting(false); }
  };

  const expectedRedirect = `${window.location.origin}/auth/discord/callback`;

  return (
    <div className="min-h-screen bg-[#050505] flex items-center justify-center p-6 grain-bg">
      <div className="w-full max-w-lg">
        {status === "processing" && (
          <Card className="bg-zinc-950 border-zinc-900 p-10 text-center space-y-4">
            <Loader2 className="w-8 h-8 text-rose-500 animate-spin mx-auto" />
            <div className="font-heading text-xl font-bold">Discord doğrulanıyor</div>
            <div className="text-sm text-zinc-500 font-mono">// AUTHENTICATING</div>
          </Card>
        )}

        {status === "error" && (
          <Card className="bg-zinc-950 border-rose-900/50 p-8 space-y-5">
            <div className="flex items-center gap-3">
              <AlertCircle className="w-6 h-6 text-rose-500" />
              <div className="font-heading text-2xl font-bold">Giriş Başarısız</div>
            </div>
            <p className="text-sm text-zinc-300 bg-zinc-900/60 p-3 border-l-2 border-rose-500 whitespace-pre-line" data-testid="callback-error-text">{error}</p>
            <div className="text-xs space-y-2">
              <div className="text-[10px] font-mono text-zinc-500 tracking-widest">// KURULUM KONTROLÜ</div>
              <div className="bg-zinc-900/60 p-3 border-l-2 border-amber-500/60 space-y-2">
                <div>Discord Developer Portal → uygulaman → <b>OAuth2 → Redirects</b> altında şu URL <b>tam olarak</b> kayıtlı olmalı:</div>
                <div className="font-mono text-amber-300 break-all" data-testid="expected-redirect-uri">{expectedRedirect}</div>
              </div>
            </div>
            <details className="text-xs">
              <summary className="cursor-pointer text-zinc-500 font-mono hover:text-rose-400">// DEBUG BİLGİSİ</summary>
              <pre className="bg-zinc-900/60 p-3 border-l-2 border-zinc-700 overflow-auto text-[10px] mt-2 whitespace-pre-wrap break-all" data-testid="callback-debug-json">{JSON.stringify(debugInfo, null, 2)}</pre>
            </details>
            <Button onClick={() => navigate("/login", { replace: true })} data-testid="back-to-login-button" className="w-full bg-rose-600 hover:bg-rose-700">Girişe Dön</Button>
          </Card>
        )}

        {status === "needs_registration" && discordData && needsFounder && (
          <Card className="bg-zinc-950 border-amber-900/50 p-8 space-y-5" data-testid="callback-bootstrap-card">
            <div>
              <div className="text-[11px] font-mono text-amber-500 tracking-widest mb-2">// FOUNDER SETUP</div>
              <h2 className="font-heading text-3xl font-bold tracking-tight">Kurucu Olarak Kaydını Tamamla</h2>
              <p className="text-sm text-zinc-400 mt-2">Sistemde henüz kurucu yok. Discord doğrulandı — sadece Hex ID gerekli, davet kodu istemez.</p>
            </div>
            <div className="flex items-center gap-3 bg-zinc-900/60 p-3 border-l-2 border-emerald-500">
              {discordData.discord_avatar && <img src={discordData.discord_avatar} alt="" className="w-10 h-10 rounded-full" />}
              <div>
                <div className="font-medium">{discordData.discord_username}</div>
                <div className="text-[10px] font-mono text-zinc-500">{discordData.discord_id}</div>
              </div>
            </div>
            <div className="space-y-3">
              <div>
                <Label className="text-xs font-mono">HEX ID</Label>
                <Input value={hexId} onChange={e=>setHexId(e.target.value)} placeholder="steam:110000..." data-testid="callback-bootstrap-hex-input" className="bg-zinc-900 border-zinc-800 mt-1.5 font-mono" />
              </div>
              <div>
                <Label className="text-xs font-mono">GÖSTERİM ADI</Label>
                <Input value={displayName} onChange={e=>setDisplayName(e.target.value)} data-testid="callback-bootstrap-display-input" className="bg-zinc-900 border-zinc-800 mt-1.5" />
              </div>
            </div>
            <Button onClick={completeBootstrap} disabled={submitting} data-testid="callback-bootstrap-submit-button" className="w-full bg-amber-600 hover:bg-amber-700 h-11 font-semibold text-black">
              {submitting && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              <KeyRound className="w-4 h-4 mr-2" /> Kurucu Olarak Kur
            </Button>
          </Card>
        )}

        {status === "needs_registration" && discordData && !needsFounder && (
          <Card className="bg-zinc-950 border-zinc-900 p-8 space-y-5">
            <div>
              <div className="text-[11px] font-mono text-rose-500 tracking-widest mb-2">// FINAL STEP</div>
              <h2 className="font-heading text-3xl font-bold tracking-tight">Kaydını Tamamla</h2>
              <p className="text-sm text-zinc-500 mt-2">Discord doğrulandı. Davet kodu ve Hex ID gerekli.</p>
            </div>
            <div className="flex items-center gap-3 bg-zinc-900/60 p-3 border-l-2 border-emerald-500">
              {discordData.discord_avatar && <img src={discordData.discord_avatar} alt="" className="w-10 h-10 rounded-full" />}
              <div>
                <div className="font-medium">{discordData.discord_username}</div>
                <div className="text-[10px] font-mono text-zinc-500">{discordData.discord_id}</div>
              </div>
            </div>
            <div className="space-y-3">
              <div><Label className="text-xs font-mono">DAVET KODU</Label>
                <Input value={inviteCode} onChange={e=>setInviteCode(e.target.value.toUpperCase())} placeholder="XXXXXXXX" data-testid="callback-invite-input" className="bg-zinc-900 border-zinc-800 mt-1.5 font-mono tracking-widest" /></div>
              <div><Label className="text-xs font-mono">HEX ID</Label>
                <Input value={hexId} onChange={e=>setHexId(e.target.value)} placeholder="steam:110000..." data-testid="callback-hex-input" className="bg-zinc-900 border-zinc-800 mt-1.5 font-mono" /></div>
              <div><Label className="text-xs font-mono">GÖSTERİM ADI</Label>
                <Input value={displayName} onChange={e=>setDisplayName(e.target.value)} data-testid="callback-display-input" className="bg-zinc-900 border-zinc-800 mt-1.5" /></div>
            </div>
            <Button onClick={completeRegistration} disabled={submitting} data-testid="callback-register-button" className="w-full bg-rose-600 hover:bg-rose-700 h-11">
              {submitting && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              <Fingerprint className="w-4 h-4 mr-2" /> Kaydı Tamamla
            </Button>
          </Card>
        )}
      </div>
    </div>
  );
}
